import { NavigationMenu as BaseNavigationMenu } from '@base-ui/react/navigation-menu';
import { useState } from 'react';
import type {
  ComponentPropsWithRef,
  ComponentPropsWithoutRef,
  JSX,
  ReactElement,
  ReactNode,
} from 'react';

import { NavigationChevronDownIcon } from '../assets/icons/NavigationChevronDownIcon.js';
import { classNames } from '../lib/classNames.js';
import * as styles from './NavigationMenu.css.js';

export type NavigationLink = {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly icon?: ReactNode;
  readonly exact?: boolean;
};
export type NavigationGroup = {
  readonly id: string;
  readonly label?: string;
  readonly items: readonly NavigationLink[];
};
export type NavigationItem =
  | (NavigationLink & { readonly type: 'link' })
  | {
      readonly type: 'menu';
      readonly id: string;
      readonly label: string;
      readonly groups: readonly NavigationGroup[];
    };
export type NavigationLinkProps = ComponentPropsWithRef<'a'> & { readonly href: string };
export type NavigationMenuProps = Omit<ComponentPropsWithoutRef<'nav'>, 'children'> & {
  readonly items: readonly NavigationItem[];
  readonly currentPath: string;
  readonly renderLink?: (props: NavigationLinkProps) => ReactElement;
};

function isCurrent(href: string, pathname: string, exact = false): boolean {
  const path = href.startsWith('#') ? href : href.split(/[?#]/)[0];
  return (
    pathname === path ||
    (!exact && path !== '/' && Boolean(path) && pathname.startsWith(`${path}/`))
  );
}
function MenuLink({
  item,
  currentPath,
  renderLink,
  topLevel = false,
}: {
  item: NavigationLink;
  currentPath: string;
  renderLink: NavigationMenuProps['renderLink'];
  topLevel?: boolean;
}): JSX.Element {
  const props: NavigationLinkProps = {
    href: item.href,
    className: topLevel ? styles.trigger : styles.link,
    'aria-current': isCurrent(item.href, currentPath, item.exact) ? 'page' : undefined,
    children: (
      <>
        {item.icon ? (
          <span className={styles.icon} aria-hidden="true">
            {item.icon}
          </span>
        ) : null}
        <span>{item.label}</span>
      </>
    ),
  };
  return (
    <BaseNavigationMenu.Link
      {...props}
      active={isCurrent(item.href, currentPath, item.exact)}
      closeOnClick
      render={renderLink?.(props)}
    />
  );
}

function MenuGroups({
  groups,
  currentPath,
  renderLink,
}: {
  groups: readonly NavigationGroup[];
  currentPath: string;
  renderLink: NavigationMenuProps['renderLink'];
}): JSX.Element {
  const visible = groups.filter((group) => group.items.length > 0);
  return (
    <div className={styles.grid} data-columns={Math.min(visible.length, 3)}>
      {visible.map((group) => (
        <section key={group.id} className={styles.group} aria-label={group.label}>
          {group.label ? <h2 className={styles.heading}>{group.label}</h2> : null}
          <ul className={styles.links}>
            {group.items.map((item) => (
              <li key={item.id}>
                <MenuLink item={item} currentPath={currentPath} renderLink={renderLink} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function NavigationMenu({
  items,
  currentPath,
  renderLink,
  className,
  ...props
}: NavigationMenuProps): JSX.Element {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  return (
    <BaseNavigationMenu.Root
      {...props}
      ref={setContainer}
      className={classNames(styles.root, className)}
    >
      <BaseNavigationMenu.List className={styles.list}>
        {items.map((item) =>
          item.type === 'link' ? (
            <BaseNavigationMenu.Item key={item.id}>
              <MenuLink item={item} currentPath={currentPath} renderLink={renderLink} topLevel />
            </BaseNavigationMenu.Item>
          ) : item.groups.some((group) => group.items.length > 0) ? (
            <BaseNavigationMenu.Item key={item.id} value={item.id}>
              <BaseNavigationMenu.Trigger
                className={styles.trigger}
                data-current={
                  item.groups.some((group) =>
                    group.items.some((link) => isCurrent(link.href, currentPath, link.exact)),
                  ) || undefined
                }
              >
                {item.label}
                <BaseNavigationMenu.Icon className={styles.caret}>
                  <NavigationChevronDownIcon />
                </BaseNavigationMenu.Icon>
              </BaseNavigationMenu.Trigger>
              <BaseNavigationMenu.Content>
                <MenuGroups
                  groups={item.groups}
                  currentPath={currentPath}
                  renderLink={renderLink}
                />
              </BaseNavigationMenu.Content>
            </BaseNavigationMenu.Item>
          ) : null,
        )}
      </BaseNavigationMenu.List>
      <BaseNavigationMenu.Portal container={container}>
        <BaseNavigationMenu.Positioner
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className={styles.positioner}
        >
          <BaseNavigationMenu.Popup className={styles.popup}>
            <BaseNavigationMenu.Viewport />
          </BaseNavigationMenu.Popup>
        </BaseNavigationMenu.Positioner>
      </BaseNavigationMenu.Portal>
    </BaseNavigationMenu.Root>
  );
}
